// What the board's roster cells say about a patient's next task and virtual visits.
import { appointmentPresentation, isTaskOverdue, localDayDelta } from './task';
import { isVisitKind, VISIT_KIND_NAME } from './visits';
import {
  OAP_OUTCOME_LABELS, oapReasonText, type RosterNextDue, type RosterVisit,
} from './dashboard';

export type RosterTone = 'bad' | 'moderate' | 'normal' | 'muted';
export interface RosterLine { text: string; tone: RosterTone }

const shortDate = (ms: number): string => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
// Whole local days from `now` to `at`; negative in the past.
const dayOffset = (at: number, now: number): number => localDayDelta(new Date(now), new Date(at));

// Lateness is isTaskOverdue's call, and a booking that explains the date speaks as it does on the task cards.
export function nextDueLine(nextDue: RosterNextDue | null, now: number): RosterLine {
  if (!nextDue) return { text: 'No open task', tone: 'muted' };
  const task = {
    status: nextDue.status ?? undefined,
    due_at: nextDue.dueAt,
    context_json: nextDue.appointment ? { appointment: nextDue.appointment } : null,
  };
  const booking = appointmentPresentation(task, new Date(now));
  if (booking?.label) return { text: booking.label, tone: booking.state === 'booked' ? 'normal' : 'moderate' };
  const due = nextDue.dueAt ? Date.parse(nextDue.dueAt) : NaN;
  if (!Number.isFinite(due)) return { text: 'No due date', tone: 'muted' };
  const late = isTaskOverdue(task, new Date(now));
  const days = dayOffset(due, now);
  if (days < 0) {
    if (!late) return { text: shortDate(due), tone: 'normal' };
    return { text: days === -1 ? '1 day late' : `${-days} days late`, tone: 'bad' };
  }
  if (days === 0) return late ? { text: 'Today, late', tone: 'bad' } : { text: 'Due today', tone: 'moderate' };
  if (days === 1) return { text: 'Tomorrow', tone: 'normal' };
  return { text: shortDate(due), tone: 'normal' };
}

export interface VisitLines { last: RosterLine; next: RosterLine; title: string | null }

const kindName = (kind: string | null): string | null => (isVisitKind(kind) ? VISIT_KIND_NAME[kind] : null);

export function visitLines(visit: RosterVisit | null | undefined, now: number): VisitLines {
  const last = visit?.last ?? null;
  const next = visit?.next ?? null;
  let lastLine: RosterLine = { text: 'Never', tone: 'muted' };
  if (last) {
    const days = -dayOffset(Date.parse(last.at), now);
    if (last.state === 'missed') lastLine = { text: 'No-show', tone: 'moderate' };
    else if (last.state === 'booked') lastLine = { text: 'Unconfirmed', tone: 'moderate' };
    else lastLine = { text: days <= 0 ? 'Today' : `${days}d ago`, tone: 'normal' };
  }
  const nextLine: RosterLine = next
    ? { text: `Next: ${shortDate(Date.parse(next.at))}`, tone: 'normal' }
    : { text: 'None booked', tone: 'muted' };
  const parts = [
    last ? `Last: ${kindName(last.kind) ?? 'visit'} on ${shortDate(Date.parse(last.at))}` : null,
    next ? `Next: ${kindName(next.kind) ?? 'visit'} on ${shortDate(Date.parse(next.at))}` : null,
  ].filter(Boolean);
  return { last: lastLine, next: nextLine, title: parts.length ? parts.join(' · ') : null };
}

// apiv2 sends outreach times as epoch seconds; older answers and the fixtures as ISO strings.
export function toMs(v: unknown): number | null {
  if (typeof v === 'number') {
    if (!Number.isFinite(v) || v <= 0) return null;
    return v < 1e12 ? v * 1000 : v;
  }
  if (typeof v !== 'string' || v === '') return null;
  if (/^\d+$/.test(v)) return toMs(Number(v));
  const ms = Date.parse(v);
  return Number.isFinite(ms) ? ms : null;
}

const ago = (ms: number, now: number): string => {
  const days = -dayOffset(ms, now);
  return days <= 0 ? 'Today' : `${days}d ago`;
};

export interface OutreachLines { human: RosterLine; ai: RosterLine; title: string }

// One column for both: the care team's last message first, the AI's under it.
export function outreachLines(humanAt: unknown, aiAt: unknown, now: number): OutreachLines {
  const human = toMs(humanAt);
  const ai = toMs(aiAt);
  return {
    human: human === null ? { text: 'Never', tone: 'muted' } : { text: ago(human, now), tone: 'normal' },
    ai: { text: `AI: ${ai === null ? 'never' : ago(ai, now)}`, tone: 'muted' },
    title: `Care team: ${human === null ? 'never' : shortDate(human)} · AI: ${ai === null ? 'never' : shortDate(ai)}`,
  };
}

export type AlertDot = 'bad' | 'high' | 'good' | 'undefined';
export interface AlertLine { dot: AlertDot; text: string; tone: RosterTone; level: string }

const alertAs = (dot: AlertDot, text: string, tone: RosterTone, level: string): AlertLine => ({
  dot, text, tone, level,
});

// Orange means an alert and nothing else. A normal-priority exception (a missed-readings task, say)
// keeps its reason on a grey dot; "Normal" only with no reason and a reading to be normal about.
export function alertLine(level: string | null | undefined, reason: string | null | undefined, hasReading: boolean): AlertLine {
  if (level === 'red') return alertAs('bad', reason || 'Urgent alert', 'normal', 'Red alert');
  if (level === 'yellow') return alertAs('high', reason || 'Alert', 'normal', 'Orange alert');
  if (reason) return alertAs('undefined', reason, 'muted', 'Open exception');
  if (level && level !== 'normal') return alertAs('undefined', '—', 'muted', 'Alert');
  if (hasReading) return alertAs('good', 'Normal', 'muted', 'No alert');
  return alertAs('undefined', 'No reading yet', 'muted', 'No alert');
}

// Calendar days, as the outreach and visit cells count them; amber past 7 days, red past 14.
export function lastReadingLine(epoch: unknown, now: number): RosterLine {
  const ms = toMs(epoch);
  if (ms === null) return { text: 'Never', tone: 'muted' };
  const days = -dayOffset(ms, now);
  let tone: RosterTone = 'normal';
  if (days > 14) tone = 'bad';
  else if (days > 7) tone = 'moderate';
  return { text: ago(ms, now), tone };
}

export interface GoalRing {
  tone: string;
  dashed: boolean;
  fraction: number;
  main: string;
  reason: string | null;
  label: string;
}

interface RowOap { outcome: string | null; reason: string | null; met: number | null; required: number | null }

// The Goals met ring: the arc is the share of the track's goals met; Missing a value is a dashed grey ring.
export function goalRing(oap: RowOap | null | undefined): GoalRing | null {
  const copy = oap?.outcome ? OAP_OUTCOME_LABELS[oap.outcome as keyof typeof OAP_OUTCOME_LABELS] : undefined;
  if (!oap || !copy) return null;
  const counted = oap.met != null && oap.required != null && oap.required > 0;
  let fraction = oap.outcome === 'met' ? 1 : 0;
  if (counted) fraction = Math.min(1, Math.max(0, (oap.met as number) / (oap.required as number)));
  return {
    tone: copy.tone,
    dashed: oap.outcome === 'missing',
    fraction,
    main: counted ? `${oap.met} of ${oap.required}` : copy.label,
    reason: oapReasonText(oap.reason),
    label: copy.label,
  };
}
